import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatCurrency, formatNumber } from '../../utils/formatters.js';

import { ConsignmentOpModal } from './ConsignmentOpModal.jsx';
import { ConsignmentDetailModal } from './ConsignmentDetailModal.jsx';

export function ConsignmentPage() {
  const { t } = useTranslation();
  const [opOpen, setOpOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const list = useQuery({ queryKey: ['inventory', 'consignment'], queryFn: () => inventoryApi.listConsignment().then((r) => r.items) });
  const rows = list.data || [];
  const totalValue = rows.reduce((s, r) => s + Number(r.value || 0), 0);
  const suppliers = new Set(rows.map((r) => r.supplierId)).size;

  const columns = [
    { key: 'product', label: t('cons.col.product'), render: (r) => <span><strong>{r.productSku}</strong> <span className="muted text-sm">{r.productName}</span></span> },
    { key: 'supplier', label: t('cons.col.supplier'), render: (r) => r.supplierName },
    { key: 'warehouse', label: t('cons.col.warehouse'), render: (r) => r.warehouseCode },
    { key: 'quantity', label: t('cons.col.quantity'), align: 'right', render: (r) => <span className="mono num">{formatNumber(r.quantity)}</span> },
    { key: 'price', label: t('cons.col.price'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.unitPrice)}</span> },
    { key: 'value', label: t('cons.col.value'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.value)}</span> },
    { key: 'actions', label: '', align: 'right', render: (r) => <Button size="sm" variant="ghost" onClick={() => setDetailId(r.id)}>{t('cons.action.view')}</Button> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('cons.title')}</h1>
          <p className="muted">{t('cons.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setOpOpen(true)}>{t('cons.action.book')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('cons.metric.lines')} value={String(rows.length)} />
        <MetricCard label={t('cons.metric.suppliers')} value={String(suppliers)} />
        <MetricCard label={t('cons.metric.value')} value={formatCurrency(totalValue)} />
      </div>

      <Table columns={columns} rows={rows} isLoading={list.isLoading} emptyMessage={t('cons.empty')} />

      <ConsignmentOpModal open={opOpen} onClose={() => setOpOpen(false)} />
      <ConsignmentDetailModal consignmentId={detailId} onClose={() => setDetailId(null)} />
    </section>
  );
}
